import { Authenticator } from '@amzn/awscat-portal-authentication-library';
import { ApplicationService, DatabaseService, DataCollectionService, PortfolioService, ServerService } from '@amzn/awscat-portfolio-assessment-service-client';
import { DeserializedApplication, QuestionnaireUpdateData, UserPermission, UserProfile } from '@amzn/awscat-portfolio-assessment-types';

import { CAT_PORTAL_PREFIX, deepCopy } from '../../../common/util';

/**
 * Returns a date at the given number of days in the future from today
 * @param daysInFuture 
 * @returns 
 */
export const getFutureDate = (daysInFuture: number): Date => {
    const date = new Date();
    date.setDate(date.getDate() + daysInFuture);
    return date;
};

/**
 * Retrieve the emails from all users who have access to the given portfolio
 * @param auth 
 * @param portfolioService 
 * @param portfolioId 
 * @returns 
 */
export const getAllPortfolioUserEmails = async (auth: Authenticator, portfolioService: PortfolioService, portfolioId: string): Promise<string[]> => {
    // ensure emails are unique
    const emails = new Set<string>();

    // get the other users who share this portfolio
    const userPerms: UserPermission[] = await portfolioService.getPortfolioPermissions(portfolioId);

    // users without an email in user permission need to user principal id to get the email
    const principalIds: string[] = userPerms.filter(userPermission => !userPermission.email)
        .map(userPermission => userPermission.principalID);

    if (principalIds.length > 0) {
        // get emails from principalIds
        const userIds = principalIds.map(id => id.replace(CAT_PORTAL_PREFIX, ''));
        const sharedUserProfiles: UserProfile[] = await auth.getUserInfoList(userIds);
        sharedUserProfiles.forEach(profile => emails.add(profile.email));
    }

    // get emails from users with email in their user permission
    userPerms.forEach(userPermission => {
        if (userPermission.email) {
            emails.add(userPermission.email);
        }
    });

    return Array.from(emails.values());
};

/**
 * Add or remove the given user email in the questionnaire owners property for all the existing questionnaires in the given portfolio
 * @param portfolioId 
 * @param userEmail 
 * @param isAddition 
 * @returns 
 */
export const updateQuestionnairesOwners = async (portfolioId: string, userEmail: string, isAddition = true): Promise<void> => {
    const applicationService = new ApplicationService();
    const serverService = new ServerService();
    const databaseService = new DatabaseService();
    const dataCollectionService = new DataCollectionService();

    const applications = await applicationService.list(portfolioId);
    const servers = await serverService.list(portfolioId);
    const databases = await databaseService.list(portfolioId);

    // retrieve all the questionnaire ids for all the assets in the given portfolio
    const appQuestionnaireIds = applications.map((app: DeserializedApplication) => app.questionnaireId).filter(qId => qId);
    const serverQuestionnaireIds = servers.map(server => server.questionnaireId).filter(qId => qId);
    const databaseQuestionnaireIds: string[] = databases.map(db => db.questionnaireId ?? '').filter(qId => !!qId);

    const questionnaireIds = [...appQuestionnaireIds, ...serverQuestionnaireIds, ...databaseQuestionnaireIds];

    // if no questionnaires have been created, there is nothing to update
    if (questionnaireIds.length === 0) {
        return;
    }

    const questionnaires = await dataCollectionService.getBatch(portfolioId, questionnaireIds);

    const questionnaireUpdateData: QuestionnaireUpdateData[] = [];
    questionnaires.forEach(questionnaire => {
        const owners = questionnaire.owners ? deepCopy(questionnaire.owners) : [];

        if (isAddition && !owners.includes(userEmail)) { // if adding an owner, verify that the owner is not already in the list
            owners.push(userEmail);
        } else if (!isAddition && owners.includes(userEmail)) { // if removing an owner, verify that the owner exists in the list
            owners.splice(owners.indexOf(userEmail, 1));
        }

        // only update when there is a change
        if (owners.length !== questionnaire.owners?.length) {
            questionnaireUpdateData.push({
                questionnaireId: questionnaire.id,
                owners
            });
        }
    });

    // only call data collection service if there is data to update
    if (questionnaireUpdateData.length > 0) {
        await dataCollectionService.update(portfolioId, questionnaireUpdateData);
    }
};
